import React, { useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { changeLanguage } from '../i18n/index.js';
import { useUser } from '../contexts/UserContext';
import { hasMenuPermission as checkMenuPermission, isSuperAdmin as checkSuperAdmin } from '../utils/adminPermissions';
import { Landmark,
  MonitorUp,
  ShoppingBag,
  Home,
  Users,
  CreditCard,
  BarChart3,
  FileText,
  Bell,
  Settings,
  HelpCircle,
  LogOut,
  ChevronsLeft,
  ChevronsRight,
  Shield,
  Building2,
  Activity,
  ShieldCheck,
  Trash2,
  Network,
  MessageSquare,
  Calendar,
  Megaphone,
  MapPin,
  Globe,
  FileCheck,
  Lock,
  Newspaper,
  UsersRound,
  MessagesSquare,
} from 'lucide-react';

const LANGUAGES = [
  { code: 'ko', label: '한국어', short: 'KO' },
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'my', label: 'မြန်မာ', short: 'MY' },
];

const NAV_GROUPS = [
  {
    key: 'overview',
    items: [
      { path: '/', key: 'dashboard', icon: Home, end: true },
      { path: '/gov-access', key: 'govAccess', icon: Landmark, isNew: true },
    ],
  },
  {
    key: 'finance',
    items: [
      { path: '/finance-settings', key: 'financeSettings', icon: Settings },
      { path: '/settlement-management', key: 'settlementManagement', icon: BarChart3 },
      { path: '/remittance-accounts', key: 'remittanceAccounts', icon: CreditCard },
    ],
  },
  {
    key: 'compliance',
    items: [
      { path: '/kyc', key: 'kyc', icon: FileText },
      { path: '/rcc-issuance', key: 'rccIssuance', icon: FileCheck },
      { path: '/policy-compliance', key: 'policyCompliance', icon: ShieldCheck, isNew: true },
      { path: '/document-expiry-management', key: 'documentExpiry', icon: Calendar, isNew: true },
    ],
  },
  {
    key: 'communication',
    items: [
      { path: '/notifications', key: 'pushNotifications', icon: Bell },
      { path: '/notices', key: 'notices', icon: Newspaper },
      { path: '/in-app-messaging', key: 'inAppMessaging', icon: Megaphone, isNew: true },
      { path: '/message-management', key: 'messageManagement', icon: MessageSquare, isNew: true },
    ],
  },
  {
    key: 'users',
    items: [
      { path: '/users', key: 'users', icon: Users },
      { path: '/community-management', key: 'communityManagement', icon: UsersRound },
      { path: '/marketplace-management', key: 'marketplaceManagement', icon: ShoppingBag, isNew: true },
    ],
  },
  {
    key: 'content',
    items: [
      { path: '/consultation-faq-management', key: 'consultationFaq', icon: MessagesSquare, isNew: true },
      { path: '/consultation-quota-monitor', key: 'consultationQuota', icon: Activity, isNew: true },
      { path: '/regional-poi-monitor', key: 'regionalPoi', icon: MapPin, isNew: true },
      { path: '/homepage-management', key: 'homepageManagement', icon: Globe, isNew: true },
    ],
  },
  {
    key: 'system',
    items: [
      { path: '/api-integration-monitoring', key: 'apiIntegration', icon: Network },
      { path: '/chat-cleanup', key: 'chatCleanup', icon: Trash2 },
      { path: '/permissions', key: 'permissions', icon: Shield, superAdminOnly: true },
      { path: '/help', key: 'help', icon: HelpCircle },
    ],
  },
];

const Sidebar = ({ isCollapsed, onToggle, onLogout }) => {
  const { user, profile } = useUser();
  const { t, i18n } = useTranslation();

  const isSuperAdmin = checkSuperAdmin(user);
  const hasMenuPermission = (menuPath) => checkMenuPermission(user, profile, menuPath);
  const currentLang = (i18n.language || 'ko').split('-')[0];

  const groups = useMemo(
    () =>
      NAV_GROUPS.map((group) => ({
        ...group,
        items: group.items.filter((item) => !item.superAdminOnly || isSuperAdmin),
      })).filter((group) => group.items.length > 0),
    [isSuperAdmin]
  );

  const handleMenuClick = (e, link) => {
    if (!hasMenuPermission(link.path)) {
      e.preventDefault();
      alert(t('sidebar.noPermission'));
    }
  };

  const email = user?.email || profile?.email || '';
  const initial = (email || 'A').charAt(0).toUpperCase();

  return (
    <motion.aside
      initial={false}
      animate={{ width: isCollapsed ? 68 : 252 }}
      transition={{ duration: 0.25, ease: 'easeInOut' }}
      className="flex h-screen flex-shrink-0 flex-col border-r border-slate-800 bg-slate-950 text-slate-300"
      style={{ maxHeight: '100vh' }}
    >
      {/* Header */}
      <div className={`flex h-14 flex-shrink-0 items-center border-b border-slate-800 ${isCollapsed ? 'justify-center px-2' : 'justify-between px-3'}`}>
        {!isCollapsed && (
          <div className="flex min-w-0 items-center gap-2.5">
            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-white p-1 ring-1 ring-amber-400/60">
              <img
                src="/images/mol-logo.png"
                alt="Kairos"
                className="h-full w-full object-contain"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.nextSibling.style.display = 'block';
                }}
              />
              <Building2 className="hidden h-4 w-4 text-slate-700" />
            </div>
            <div className="min-w-0">
              <div className="truncate text-[13px] font-bold leading-tight text-white">Kairos Admin</div>
              <div className="truncate text-[10px] leading-tight text-slate-500">{t('sidebar.adminConsole')}</div>
            </div>
          </div>
        )}
        <button
          type="button"
          onClick={onToggle}
          title={isCollapsed ? t('sidebar.expand') : t('sidebar.collapse')}
          className="flex !h-8 !min-h-0 !w-8 !min-w-0 flex-shrink-0 items-center justify-center rounded-md !p-0 text-slate-400 transition-colors hover:bg-slate-800 hover:text-white"
        >
          {isCollapsed ? <ChevronsRight size={16} /> : <ChevronsLeft size={16} />}
        </button>
      </div>

      {/* Navigation */}
      <nav
        className="sidebar-scroll flex-1 overflow-y-auto overflow-x-hidden px-2 py-2"
        style={{ minHeight: 0, scrollbarWidth: 'thin', scrollbarColor: '#334155 transparent' }}
      >
        {groups.map((group, gi) => (
          <div key={group.key} className={gi > 0 ? 'mt-3' : ''}>
            {isCollapsed ? (
              gi > 0 && <div className="mx-3 mb-2 border-t border-slate-800" />
            ) : (
              <div className="mb-1 px-2.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                {t(`sidebar.group.${group.key}`)}
              </div>
            )}
            <div className="space-y-0.5">
              {group.items.map((link) => {
                const allowed = hasMenuPermission(link.path);
                const label = t(`sidebar.${link.key}`);
                const Icon = link.icon;
                return (
                  <NavLink
                    key={link.path}
                    to={link.path}
                    end={link.end}
                    onClick={(e) => handleMenuClick(e, link)}
                    title={!allowed ? t('sidebar.noAccessTitle') : isCollapsed ? label : undefined}
                    className={({ isActive }) =>
                      `group relative flex h-8 items-center rounded-md text-[13px] transition-colors ${
                        isCollapsed ? 'justify-center' : 'gap-2.5 px-2.5'
                      } ${
                        !allowed
                          ? 'cursor-not-allowed text-slate-600'
                          : isActive
                            ? 'bg-indigo-500/15 font-semibold text-white'
                            : 'text-slate-400 hover:bg-slate-800/70 hover:text-slate-100'
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        {isActive && allowed && (
                          <span className="absolute left-0 top-1.5 h-5 w-[3px] rounded-r bg-indigo-400" />
                        )}
                        <Icon
                          size={16}
                          className={`flex-shrink-0 ${isActive && allowed ? 'text-indigo-300' : ''}`}
                        />
                        {!isCollapsed && (
                          <>
                            <span className="min-w-0 flex-1 truncate">{label}</span>
                            {allowed && link.path === '/' && (
                              <span
                                role="button"
                                tabIndex={0}
                                title={t('sidebar.openDisplay')}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  window.open('/display', 'kairos-display');
                                }}
                                className="flex-shrink-0 rounded p-1 text-slate-500 hover:bg-slate-700 hover:text-slate-100"
                              >
                                <MonitorUp size={13} />
                              </span>
                            )}
                            {!allowed ? (
                              <Lock size={12} className="flex-shrink-0 text-slate-600" />
                            ) : (
                              link.isNew && (
                                <span className="flex-shrink-0 rounded bg-indigo-500/20 px-1.5 py-px text-[9px] font-bold uppercase leading-4 text-indigo-300">
                                  {t('sidebar.newBadge')}
                                </span>
                              )
                            )}
                          </>
                        )}
                        {isCollapsed && link.isNew && allowed && (
                          <span className="absolute right-2.5 top-1.5 h-1.5 w-1.5 rounded-full bg-indigo-400" />
                        )}
                      </>
                    )}
                  </NavLink>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className="flex-shrink-0 space-y-2 border-t border-slate-800 p-2">
        {isCollapsed ? (
          <button
            type="button"
            onClick={() => {
              const idx = LANGUAGES.findIndex((l) => l.code === currentLang);
              changeLanguage(LANGUAGES[(idx + 1) % LANGUAGES.length].code);
            }}
            title={t('sidebar.language')}
            className="flex !h-8 !min-h-0 w-full items-center justify-center rounded-md !p-0 text-[10px] font-bold text-slate-300 ring-1 ring-slate-700 hover:bg-slate-800"
          >
            {(LANGUAGES.find((l) => l.code === currentLang) || LANGUAGES[0]).short}
          </button>
        ) : (
          <div>
            <div className="mb-1 flex items-center gap-1 px-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
              <Globe size={11} />
              {t('sidebar.language')}
            </div>
            <div className="grid grid-cols-3 gap-0.5 rounded-lg bg-slate-900 p-0.5 ring-1 ring-slate-800">
              {LANGUAGES.map(({ code, label }) => (
                <button
                  key={code}
                  type="button"
                  onClick={() => changeLanguage(code)}
                  className={`!h-7 !min-h-0 truncate rounded-md !px-1 !py-0 text-[11px] font-semibold transition-colors ${
                    currentLang === code ? 'bg-indigo-500 text-white shadow-sm' : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className={`flex items-center rounded-lg bg-slate-900/60 ${isCollapsed ? 'flex-col gap-1 p-1' : 'gap-2 p-1.5'}`}>
          <span
            className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-indigo-500 text-[11px] font-bold text-white"
            title={email}
          >
            {initial}
          </span>
          {!isCollapsed && (
            <div className="min-w-0 flex-1">
              <div className="truncate text-[12px] font-semibold leading-tight text-slate-200">{email || 'admin'}</div>
              <div className="truncate text-[10px] leading-tight text-slate-500">
                {isSuperAdmin ? t('sidebar.superAdmin') : t('sidebar.admin')}
              </div>
            </div>
          )}
          <button
            type="button"
            onClick={onLogout}
            title={t('sidebar.logout')}
            className="flex !h-7 !min-h-0 !w-7 !min-w-0 flex-shrink-0 items-center justify-center rounded-md !p-0 text-slate-400 transition-colors hover:bg-red-500/15 hover:text-red-400"
          >
            <LogOut size={14} />
          </button>
        </div>
      </div>
    </motion.aside>
  );
};

export default Sidebar;
